import { useQuery } from 'src/shared/lib/useQuery';
import { useCallback } from 'react';
import { BASE_PATH } from 'src/features/flows/queries/useFlow';
import { useOrg } from 'src/shared/contexts/OrgContext';
import { getActionConfig } from 'src/features/flows/lib/general';
import { genQueryKey } from 'src/shared/lib/query.utils';
import { Flow } from 'src/features/flows/types/flow.types';
import { FlowActionConfig } from 'src/features/flows/types/flowAction.types';
import { QueryResult } from 'src/shared/types/util.types';

type lazy = 'full' | 'lazy';

export function useFlowActions(flow: Flow | undefined, lazy: lazy): QueryResult<FlowActionConfig[]>;
export function useFlowActions(flow: Flow | undefined, lazy: lazy, selectKey: string): QueryResult<FlowActionConfig>;
export function useFlowActions(
  flow: Flow | undefined,
  lazy: lazy = 'full',
  selectKey?: string
): QueryResult<FlowActionConfig | FlowActionConfig[]> {
  const { orgFetch, currentOrg } = useOrg();

  const queryKey = flow?.id ? genQueryKey(currentOrg.id, flow.id) : '';

  const selectFlowActions = useCallback(
    (data: FlowActionConfig[]) => {
      if (selectKey && selectKey.trim()) {
        const [actionId, type] = selectKey.split(':');
        if (actionId && type) {
          return getActionConfig(data, actionId, type);
        }
      }
      return data;
    },
    [selectKey]
  );

  return useQuery({
    queryKey: [`flow-actions`, lazy, queryKey],
    queryFn: getData,
    staleTime: 1000 * 60 * 15,
    enabled: !!flow?.integrations?.length,
    select: selectFlowActions,
  });

  async function getData() {
    const res = await orgFetch<FlowActionConfig[]>(
      `${BASE_PATH}/actionsV2?flowId=${flow?.id}&lazy=${lazy === 'full' ? false : true}`
    );
    if (!res.ok) {
      throw new Error('failed to fetch flow actions');
    }
    return await res.json();
  }
}
